/**
 * @unscoped — 배포로 사라진 청크를 만난 탭을 **한 번만** 새로 고쳐 되살린다 (2026-08-19).
 *
 * 증상 — 오래 열어 둔 탭에서 한글 첨부를 열면 화면 대신 이 문구가 떴다:
 *   `Failed to fetch dynamically imported module: https://…/assets/office-hwp-DLa9fySz.js`
 *
 * 원인 — 자산 이름에 내용 해시가 붙어 **배포마다 이름이 바뀌고**, 운영은 참조 도달성으로 옛
 * 자산을 정리한다(`scripts/ops/prune-frontend-assets.py`). 그런데 탭은 열릴 때 받은 **옛 진입
 * 번들**을 메모리에 들고 있어, 게으른 `import()` 를 할 때 이미 지워진 이름을 요청한다.
 * 서버는 멀쩡하다 — 운영 index.html 은 `cache-control: no-cache` 라 새로고침만 하면 새 해시를
 * 준다. 낡은 것은 탭뿐이다. 하루에 여러 번 배포하므로 탭을 몇 번의 배포에 걸쳐 열어 두면 걸린다.
 *
 * 처방 — vite 의 preload 도우미는 실패를 `vite:preloadError`(cancelable) 로 알린다
 * (vite 5 의 `__vitePreload` 는 `baseModule().catch(handlePreloadError)` 라 **모듈 fetch 실패도**
 * 이 이벤트로 온다). 그것을 받아 새로고침하면 새 index.html → 새 해시라 그대로 열린다.
 *
 * **한 번만** 인 이유 — 자산이 진짜로 없거나 네트워크가 끊긴 경우엔 새로고침해도 또 실패한다.
 * 표식 없이 두면 무한 새로고침이 된다. 그래서 sessionStorage(탭 단위)에 시각을 남기고,
 * 쿨다운 안에서 또 나면 **고치지 않고 오류를 원래대로 흘려보낸다**(ErrorBoundary 가 받는 기존 동작).
 * 표식을 남기지 못하는 환경(사생활 보호 모드 등)에서도 같은 이유로 고치지 않는다 —
 * 표식이 없으면 "이미 고쳤다" 를 알 길이 없어 반복이 된다.
 */

import { reportError } from './telemetry';

/** 새로고침 표식 키. sessionStorage 라 탭 단위 — 다른 탭·다음 방문에 영향 없다. */
export const CHUNK_RELOAD_MARK_KEY = 'axi:chunk-reload-at';

/** 쿨다운. 이 안에서 또 실패하면 새로고침 대신 오류를 그대로 흘린다. */
export const CHUNK_RELOAD_COOLDOWN_MS = 60_000;

/** vite 가 실패 원인을 싣는 자리 — `e.payload = err`. */
type PreloadErrorEvent = Event & { payload?: unknown };

interface GuardOptions {
  /** 새로고침 수행자. 테스트에서 갈아 끼운다(jsdom 은 실제 navigation 을 못 한다). */
  reload?: () => void;
  /** 현재 시각. 테스트에서 쿨다운을 흉내 내려고 연다. */
  now?: () => number;
}

let installed = false;

function readMark(): number | null {
  try {
    const raw = window.sessionStorage.getItem(CHUNK_RELOAD_MARK_KEY);
    if (raw === null) return null;
    const at = Number(raw);
    return Number.isFinite(at) ? at : null;
  } catch {
    return null;
  }
}

/** 표식을 남긴다. 못 남기면 false — 호출부는 새로고침을 포기한다(무한 반복 방지). */
function writeMark(at: number): boolean {
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_MARK_KEY, String(at));
    return true;
  } catch {
    return false;
  }
}

function toError(payload: unknown): Error {
  if (payload instanceof Error) return payload;
  return new Error(typeof payload === 'string' ? payload : 'dynamic import failed');
}

/**
 * `vite:preloadError` 감시를 건다. 반환값은 해제 함수(테스트·HMR 용).
 * main.tsx 에서 한 번 부른다. 두 번 불러도 두 번 걸리지 않는다.
 */
export function installChunkReloadGuard(options: GuardOptions = {}): () => void {
  if (typeof window === 'undefined') return () => {};
  if (installed) return () => {};
  installed = true;

  const now = options.now ?? (() => Date.now());
  const reload = options.reload ?? (() => { window.location.reload(); });
  let reloadRequested = false;

  const handler = (event: Event): void => {
    // 이미 새로고침이 걸렸다 — 뒤따르는 실패는 오류 화면만 막고 조용히 넘긴다.
    if (reloadRequested) {
      event.preventDefault();
      return;
    }

    const mark = readMark();
    if (mark !== null && now() - mark < CHUNK_RELOAD_COOLDOWN_MS) return; // 방금 고쳤는데 또다
    if (!writeMark(now())) return;                                        // 표식 불가 — 반복 위험

    reloadRequested = true;
    // vite 가 오류를 다시 던지지 않게 막는다 — 곧 페이지가 통째로 바뀐다.
    event.preventDefault();
    // 얼마나 자주 나는지는 남긴다(keepalive 라 unload 를 넘어간다).
    reportError(toError((event as PreloadErrorEvent).payload), {
      source: 'chunk-preload-error',
      extra: { href: window.location.pathname },
    });
    reload();
  };

  window.addEventListener('vite:preloadError', handler);
  return () => {
    window.removeEventListener('vite:preloadError', handler);
    installed = false;
  };
}

/** test 격리 — installed flag 리셋 후 다시 installChunkReloadGuard 호출 가능. */
export function _resetChunkReloadGuardForTests(): void {
  installed = false;
}
